import { useEffect, useState } from 'react';
import { resolveSiteTimeZone } from '../domain/siteTime/siteTime';
import { sitesApi } from '../services/sitesApi';

const cache = new Map<string, string>();

/** Load sites.timezone for a site id. Missing/invalid zones stay null (formatters render UTC). */
export function useSiteTimeZone(siteId?: string | null): string | null {
  const scoped = String(siteId || '').trim();
  const [timeZone, setTimeZone] = useState<string | null>(
    scoped ? (cache.get(scoped) || null) : null,
  );

  useEffect(() => {
    if (!scoped) {
      setTimeZone(null);
      return;
    }
    if (cache.has(scoped)) {
      setTimeZone(cache.get(scoped) || null);
      return;
    }
    let cancelled = false;
    Promise.resolve()
      .then(() => {
        if (typeof sitesApi.getSite !== 'function') {
          throw new Error('sitesApi.getSite unavailable');
        }
        return sitesApi.getSite(scoped);
      })
      .then((site) => {
        const resolved = resolveSiteTimeZone(site);
        if (resolved) cache.set(scoped, resolved);
        if (!cancelled) setTimeZone(resolved);
      })
      .catch(() => {
        if (!cancelled) setTimeZone(null);
      });
    return () => {
      cancelled = true;
    };
  }, [scoped]);

  return timeZone;
}

export function readCachedSiteTimeZone(siteId?: string | null): string | null {
  const scoped = String(siteId || '').trim();
  if (!scoped || !cache.has(scoped)) return null;
  return cache.get(scoped) || null;
}
